import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import { reviewsApi } from "./api";
import { useToast } from "@/app/providers/Toaster";
import { usePermission } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

const STATUS_FILTERS = [
  { value: "pending", label: "Pendientes" },
  { value: "approved", label: "Aprobadas" },
  { value: "rejected", label: "Rechazadas" },
];

export function ReviewsScreen() {
  const qc = useQueryClient();
  const { push } = useToast();
  const canModerate = usePermission(PERMISSIONS.CONTENT_MANAGE);
  const [status, setStatus] = useState("pending");

  const queue = useQuery({
    queryKey: ["content", "reviews", status],
    queryFn: () => reviewsApi.queue(status),
  });

  const moderate = useMutation({
    mutationFn: ({ id, action }: { id: number; action: "approve" | "reject" }) =>
      reviewsApi.moderate(id, action),
    onSuccess: (_d, v) => {
      push({ kind: "success", message: v.action === "approve" ? "Reseña aprobada." : "Reseña rechazada." });
      qc.invalidateQueries({ queryKey: ["content", "reviews"] });
    },
    onError: () => push({ kind: "error", message: "No se pudo moderar la reseña." }),
  });

  const rows = queue.data ?? [];

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Comunidad · Reseñas</p>
          <h1 className="nx-h1">Moderación de reseñas</h1>
          <p className="text-sm text-fg-muted mt-1">
            Reseñas públicas de asistentes (con opt-in). Solo las aprobadas aparecen en la página
            del evento. El feedback privado al productor nunca se muestra acá ni se hace público.
          </p>
        </div>
        <button onClick={() => queue.refetch()} className="nx-btn-secondary" disabled={queue.isFetching}>
          <RefreshCw size={14} className={queue.isFetching ? "animate-spin" : ""} /> Actualizar
        </button>
      </header>

      <div className="flex items-center gap-1.5">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatus(f.value)}
            className={
              "px-3 py-1.5 rounded-md text-xs font-medium transition-colors " +
              (status === f.value ? "bg-accent text-fg-inverted" : "bg-bg-subtle text-fg-muted hover:text-fg")
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {queue.isLoading ? (
        <div className="text-sm text-fg-subtle py-10 text-center">Cargando reseñas…</div>
      ) : queue.isError ? (
        <div className="text-sm text-danger py-10 text-center">
          Error al cargar.{" "}
          <button className="underline" onClick={() => queue.refetch()}>Reintentar</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border-subtle rounded-lg py-12 text-center text-sm text-fg-muted">
          No hay reseñas {STATUS_FILTERS.find((f) => f.value === status)?.label.toLowerCase()}.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((r) => (
            <div key={r.id} className="border border-border-subtle rounded-lg p-4 bg-bg-base">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-fg">
                    {r.author}
                    {r.eventName && <span className="text-fg-subtle font-normal"> · {r.eventName}</span>}
                  </div>
                  <div className="text-2xs text-fg-subtle">
                    {new Date(r.createdAt).toLocaleString("es-CL")}
                  </div>
                </div>
                {status === "pending" && canModerate && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      className="nx-btn-danger"
                      disabled={moderate.isPending}
                      onClick={() => moderate.mutate({ id: r.id, action: "reject" })}
                    >
                      <XCircle size={14} /> Rechazar
                    </button>
                    <button
                      className="nx-btn-primary"
                      disabled={moderate.isPending}
                      onClick={() => moderate.mutate({ id: r.id, action: "approve" })}
                    >
                      <CheckCircle2 size={14} /> Aprobar
                    </button>
                  </div>
                )}
              </div>

              {r.review && <p className="text-sm text-fg mt-2 whitespace-pre-wrap">{r.review}</p>}

              {r.photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {r.photos.map((url) => (
                    <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                      <img
                        src={url}
                        alt="Foto de la reseña"
                        className="w-20 h-20 object-cover rounded-md border border-border-subtle"
                      />
                    </a>
                  ))}
                </div>
              )}

              {r.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {r.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex text-2xs bg-bg-subtle border border-border-subtle text-fg-muted rounded px-1.5 py-0.5"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
